import { useState } from 'react'
import Slider from 'react-slick'
import type { Settings } from 'react-slick'
import styled from 'styled-components'

import type { components } from '../../../constants/schema'

import { useNavigate } from '../../../hooks/useNavigate'

import { CenterItem } from '../../../components/common'
import { Text } from '../../../components/text'

import 'slick-carousel/slick/slick.css'

interface HomeBannerProps {
  banners: components['schemas']['HomeItemsDto']['homeBanners']
}

const SETTINGS: Settings = {
  arrows: false,
  centerMode: true,
  centerPadding: '28px',
  initialSlide: 0,
}

export function HomeBanner({ banners }: HomeBannerProps) {
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [isDrag, setIsDrag] = useState<boolean>(false)

  const navigate = useNavigate()

  return (
    <HomeBannerContainer>
      {banners.length === 1 ? (
        <OneImageBox>
          <ImageBox onClick={() => navigate(banners[0].targetUrl)}>
            <img src={banners[0].imageUri} alt={banners[0].title} />
          </ImageBox>
        </OneImageBox>
      ) : (
        <>
          <Slider
            {...SETTINGS}
            infinite={banners.length > 2}
            beforeChange={(_, next) => {
              setIsDrag(true)
              setCurrentSlide(next)
            }}
            afterChange={() => setIsDrag(false)}
          >
            {banners.map(({ id, imageUri, title, targetUrl }, index) => {
              const length = banners.length

              const isNext = index === currentSlide + 1 || (length > 2 && currentSlide === length - 1 && index === 0)
              const isPrev = index === currentSlide - 1 || (length > 2 && currentSlide === 0 && index === length - 1)

              const navigateUrl = () => {
                if (isDrag) return
                navigate(targetUrl)
              }

              return (
                <SlideItem key={id} $isPrev={isPrev} $isNext={isNext} onClick={navigateUrl}>
                  <ImageBox>
                    <img src={imageUri} alt={title} />
                  </ImageBox>
                </SlideItem>
              )
            })}
          </Slider>
          <Pagination>
            <Text size={12}>
              {currentSlide + 1} / {banners.length}
            </Text>
          </Pagination>
        </>
      )}
    </HomeBannerContainer>
  )
}

const HomeBannerContainer = styled.div`
  position: relative;
  margin-top: 16px;
  touch-action: none;
  .slick-slide {
    aspect-ratio: 1/1.18;
    transition: 0.3s;
    &.slick-center {
      transform: scale(1);
    }
  }
`

const ImageBox = styled.div`
  overflow: hidden;
  width: 100%;
  height: 100%;
  aspect-ratio: 1/1.18;
  border-radius: 24px;
  cursor: pointer;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const OneImageBox = styled(ImageBox)`
  width: calc(100% - 16px * 2);
  margin: 0 auto;
`

const SlideItem = styled.div<{ $isPrev: boolean; $isNext: boolean }>`
  overflow: hidden;
  position: relative;
  height: 100%;
  aspect-ratio: 1/1.18;
  border-radius: 24px;
  ${({ $isPrev, $isNext }) =>
    `
      ${$isPrev ? `transform: scale(0.85) translateX(calc(${Math.min(window.innerWidth, 500) * 0.075}px - 12px));` : ``}
      ${$isNext ? `transform: scale(0.85) translateX(calc((${Math.min(window.innerWidth, 500) * 0.075}px - 12px) * -1));` : ``}
    `}
  transition: 0.3s;
`

const Pagination = styled(CenterItem)`
  position: absolute;
  top: 16px;
  right: calc(28px + 16px);
  padding: 6px 8px;
  border-radius: 16px;
  background-color: rgba(255, 255, 255, 0.55);
`
